import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  CheckCircle, 
  Award, 
  Calendar,
  Clock,
  BookOpen,
  Star,
  Filter,
  Download,
  Share,
  BarChart3,
  TrendingUp
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useUserProgress, useUserAchievements } from '@/hooks/useUserProgress';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';

export const CompletedTab: React.FC = () => {
  const [filterBy, setFilterBy] = useState('all');
  const { user } = useAuth();
  
  // Get completed content with node details
  const { data: completedContent, isLoading: completedLoading } = useQuery({
    queryKey: ['completed-content', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('user_progress')
        .select(`
          *,
          nodes!inner(
            id,
            title,
            node_type,
            difficulty_level,
            estimated_study_time,
            content_type,
            disciplines!inner(name)
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Get user achievements
  const { data: achievements, isLoading: achievementsLoading } = useUserAchievements();

  // Get disciplines for filter
  const { data: disciplines } = useQuery({
    queryKey: ['disciplines'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('disciplines')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  const getDifficultyLabel = (level: number) => {
    const labels = ['', 'Foundational', 'Intermediate', 'Advanced', 'Research', 'Expert'];
    return labels[level] || 'Intermediate';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Unknown';
    }
  };

  const getAchievementIcon = (achievementType: string) => {
    const icons = {
      'first_view': '👀',
      'first_completion': '🎯',
      'discipline_explorer': '🗺️',
      'active_learner': '📚',
      'knowledge_seeker': '🔍',
      'content_creator': '✍️',
      'community_helper': '🤝'
    };
    return icons[achievementType] || '🏆';
  };

  const getAchievementCategory = (achievementType: string) => {
    if (achievementType.includes('first')) return 'milestone';
    if (achievementType.includes('explorer') || achievementType.includes('seeker')) return 'discovery';
    if (achievementType.includes('active') || achievementType.includes('streak')) return 'engagement';
    return 'mastery';
  };

  // Calculate learning stats from real data
  const learningStats = React.useMemo(() => {
    if (!completedContent) return {
      totalCompleted: 0,
      totalTimeSpent: 0,
      averageRating: 0,
      disciplines: 0,
      achievements: achievements?.length || 0
    };

    const totalTimeMinutes = completedContent.reduce((acc, item) => 
      acc + Math.floor((item.time_spent_seconds || 0) / 60), 0
    );
    
    const uniqueDisciplines = new Set(
      completedContent.map(item => item.nodes?.disciplines?.name).filter(Boolean)
    ).size;

    return {
      totalCompleted: completedContent.length,
      totalTimeSpent: totalTimeMinutes,
      averageRating: 4.2, // We'd need a ratings system for this
      disciplines: uniqueDisciplines,
      achievements: achievements?.length || 0
    };
  }, [completedContent, achievements]);

  const filteredContent = React.useMemo(() => {
    if (!completedContent) return [];
    if (filterBy === 'all') return completedContent;
    
    return completedContent.filter(item => 
      item.nodes?.disciplines?.name.toLowerCase() === filterBy.toLowerCase()
    );
  }, [completedContent, filterBy]);

  const isLoading = completedLoading || achievementsLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Loading Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6 text-center">
                <Skeleton className="h-8 w-12 mx-auto mb-2" />
                <Skeleton className="h-4 w-16 mx-auto" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Loading Tabs */}
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <div className="space-y-2">
                        <Skeleton className="h-5 w-48" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                      <Skeleton className="h-6 w-20" />
                    </div>
                    <div className="flex gap-4">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-green-600">{learningStats.totalCompleted}</div>
            <div className="text-sm text-black">Completed</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-black">{Math.round(learningStats.totalTimeSpent / 60)}h</div>
            <div className="text-sm text-black">Total Time</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-black">{learningStats.averageRating.toFixed(1)}</div>
            <div className="text-sm text-black">Avg Rating</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-black">{learningStats.disciplines}</div>
            <div className="text-sm text-black">Disciplines</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-black">{learningStats.achievements}</div>
            <div className="text-sm text-black">Achievements</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Completed Content and Achievements */}
      <Tabs defaultValue="content" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="content" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Completed Content ({filteredContent.length})
          </TabsTrigger>
          <TabsTrigger value="achievements" className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            Achievements ({achievements?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Completed Content Tab */}
        <TabsContent value="content" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-black" />
              <select 
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                className="text-sm border border-gray-300 rounded px-2 py-1"
                aria-label="Filter by discipline"
              >
                <option value="all">All Disciplines</option>
                {disciplines?.map((discipline) => (
                  <option key={discipline.id} value={discipline.name}>
                    {discipline.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
              <Button variant="outline" size="sm">
                <Share className="h-4 w-4 mr-1" />
                Share
              </Button>
            </div>
          </div>

          {/* Content List */}
          <div className="space-y-4">
            {filteredContent.length > 0 ? (
              filteredContent.map((item) => {
                const node = item.nodes;
                const timeSpentMinutes = Math.floor((item.time_spent_seconds || 0) / 60);
                
                return (
                  <Card key={item.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <h4 className="font-medium text-black">{node?.title}</h4>
                            <Badge variant="outline" className="bg-white text-green-700 border-green-200">
                              Completed
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-black">
                            <span className="flex items-center gap-1">
                              <BookOpen className="h-3 w-3" />
                              {node?.disciplines?.name || 'General'}
                            </span>
                            <span className="flex items-center gap-1">
                              <BarChart3 className="h-3 w-3" />
                              {getDifficultyLabel(node?.difficulty_level || 2)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {timeSpentMinutes}m spent
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(item.completed_at)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-black">Type:</span>
                            <Badge variant="secondary">{node?.content_type || node?.node_type}</Badge>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 text-sky-500">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star key={star} className="h-4 w-4 fill-current" />
                            ))}
                          </div>
                          <Button variant="ghost" size="sm">
                            Review
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8 space-y-4">
                    <CheckCircle className="h-12 w-12 mx-auto text-gray-300" />
                    <div>
                      <h3 className="font-medium text-black mb-1">No completed content</h3>
                      <p className="text-sm text-black">
                        {filterBy === 'all' 
                          ? 'Start learning to see your completed content here'
                          : `No completed content in ${filterBy}`
                        }
                      </p>
                    </div>
                    <Button>Start Learning</Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Achievements Tab */}
        <TabsContent value="achievements" className="space-y-4">
          <div className="space-y-4">
            {achievements && achievements.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {achievements.map((achievement) => (
                  <Card key={achievement.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="text-3xl">
                          {getAchievementIcon(achievement.achievement_type)}
                        </div>
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-black">
                              {achievement.achievement_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </h4>
                            <Badge variant="outline" className={
                              getAchievementCategory(achievement.achievement_type) === 'milestone' 
                                ? 'bg-white text-black border-blue-200'
                                : getAchievementCategory(achievement.achievement_type) === 'discovery'
                                ? 'bg-white text-black border-purple-200'
                                : 'bg-white text-green-700 border-green-200'
                            }>
                              {getAchievementCategory(achievement.achievement_type)}
                            </Badge>
                          </div>
                          
                          <p className="text-sm text-black">
                            Earned {formatDistanceToNow(new Date(achievement.earned_at), { addSuffix: true })}
                          </p>
                          
                          {achievement.points_awarded && (
                            <div className="flex items-center gap-1 text-sm font-medium text-black">
                              <Award className="h-3 w-3" />
                              +{achievement.points_awarded} points
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8 space-y-4">
                    <Award className="h-12 w-12 mx-auto text-gray-300" />
                    <div>
                      <h3 className="font-medium text-black mb-1">No achievements yet</h3>
                      <p className="text-sm text-black">Complete learning activities to earn your first achievement</p>
                    </div>
                    <Button>Start Learning</Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}; 